export const DEFAULT_BHG_NOTIFICATION_TIME = "05:00";
export const DEFAULT_BHG_NOTIFICATION_TIMEZONE = "America/New_York";

export const BHG_US_TIMEZONE_OPTIONS = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Anchorage", label: "Alaska (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii-Aleutian (HAT)" },
] as const;

const BHG_US_TIMEZONE_SET = new Set<string>(BHG_US_TIMEZONE_OPTIONS.map((option) => option.value));

export function isValidBhgNotificationTime(value: string) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value.trim());
}

export function isValidBhgNotificationTimezone(value: string) {
  return BHG_US_TIMEZONE_SET.has(value.trim());
}

export function normalizeBhgNotificationTimezone(value: string | null | undefined) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return DEFAULT_BHG_NOTIFICATION_TIMEZONE;
  }
  return isValidBhgNotificationTimezone(trimmed) ? trimmed : DEFAULT_BHG_NOTIFICATION_TIMEZONE;
}

function parseBhgTime(value: string) {
  const match = String(value ?? "")
    .trim()
    .match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) {
    return null;
  }

  return {
    hour: Number(match[1]),
    minute: Number(match[2]),
  };
}

function getDateTimePartsInTimezone(date: Date, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const get = (type: string) => {
    const part = parts.find((entry) => entry.type === type);
    const parsed = Number(part?.value ?? "");
    return Number.isFinite(parsed) ? parsed : null;
  };

  const year = get("year");
  const month = get("month");
  const day = get("day");
  const hour = get("hour");
  const minute = get("minute");
  const second = get("second");

  if (
    year === null ||
    month === null ||
    day === null ||
    hour === null ||
    minute === null ||
    second === null
  ) {
    throw new Error(`Unable to resolve date parts for timezone: ${timezone}`);
  }

  return { year, month, day, hour, minute, second };
}

function getDatePartsInTimezone(date: Date, timezone: string) {
  const parts = getDateTimePartsInTimezone(date, timezone);
  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
  };
}

function getTimezoneOffsetMs(date: Date, timezone: string) {
  const parts = getDateTimePartsInTimezone(date, timezone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
    0
  );
  return asUtc - date.getTime();
}

function resolveTimezoneLocalDateTimeUtc(input: {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  timezone: string;
}) {
  const utcGuess = new Date(
    Date.UTC(input.year, input.month - 1, input.day, input.hour, input.minute, 0, 0)
  );
  const firstOffset = getTimezoneOffsetMs(utcGuess, input.timezone);
  const firstCandidate = new Date(utcGuess.getTime() - firstOffset);
  const secondOffset = getTimezoneOffsetMs(firstCandidate, input.timezone);
  if (secondOffset === firstOffset) {
    return firstCandidate;
  }
  return new Date(utcGuess.getTime() - secondOffset);
}

function formatTimeUtc(date: Date) {
  return date.toISOString().slice(11, 16);
}

function formatTimeInTimezone(date: Date, timezone: string) {
  const parts = getDateTimePartsInTimezone(date, timezone);
  return `${String(parts.hour).padStart(2, "0")}:${String(parts.minute).padStart(2, "0")}`;
}

export function convertBhgNotificationTimeToUtc(
  notificationTime: string,
  notificationTimezone: string,
  referenceDate = new Date()
) {
  const parsed = parseBhgTime(notificationTime);
  if (!parsed) {
    return notificationTime;
  }

  const timezone = normalizeBhgNotificationTimezone(notificationTimezone);
  const today = getDatePartsInTimezone(referenceDate, timezone);
  const utcDate = resolveTimezoneLocalDateTimeUtc({
    year: today.year,
    month: today.month,
    day: today.day,
    hour: parsed.hour,
    minute: parsed.minute,
    timezone,
  });
  return formatTimeUtc(utcDate);
}

export function convertBhgNotificationTimeToLocal(
  notificationTime: string,
  notificationTimezone: string,
  referenceDate = new Date()
) {
  const parsed = parseBhgTime(notificationTime);
  if (!parsed) {
    return notificationTime;
  }

  const timezone = normalizeBhgNotificationTimezone(notificationTimezone);
  const utcToday = getDatePartsInTimezone(referenceDate, "UTC");
  const utcDate = new Date(
    Date.UTC(utcToday.year, utcToday.month - 1, utcToday.day, parsed.hour, parsed.minute, 0, 0)
  );
  return formatTimeInTimezone(utcDate, timezone);
}

const WEEKDAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type BhgNotificationWindow = {
  earliestClosingDay: (typeof WEEKDAY_ORDER)[number];
  latestNotificationTime: string;
};

export function getBhgNotificationWindow(
  dosingHoursJson: string | null
): BhgNotificationWindow | null {
  if (!dosingHoursJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(dosingHoursJson) as Record<string, unknown>;
    const ranges = WEEKDAY_ORDER.flatMap((day) => {
      const value = parsed[day];
      if (typeof value !== "string") {
        return [];
      }

      const range = parseDosingRange(value);
      return range ? [{ day, ...range }] : [];
    });

    if (ranges.length === 0) {
      return null;
    }

    ranges.sort((left, right) => {
      if (left.endMinutes !== right.endMinutes) {
        return left.endMinutes - right.endMinutes;
      }
      return WEEKDAY_ORDER.indexOf(left.day) - WEEKDAY_ORDER.indexOf(right.day);
    });

    const earliestClosingRange = ranges[0];
    const midpointMinutes =
      earliestClosingRange.startMinutes +
      Math.floor((earliestClosingRange.endMinutes - earliestClosingRange.startMinutes) / 2);

    return {
      earliestClosingDay: earliestClosingRange.day,
      latestNotificationTime: formatMinutesAsTime(midpointMinutes),
    };
  } catch {
    return null;
  }
}

export function getBhgNotificationTimeError(
  notificationTime: string,
  dosingHoursJson: string | null
): string | undefined {
  if (!isValidBhgNotificationTime(notificationTime)) {
    return "Notification time must be a valid time.";
  }

  const window = getBhgNotificationWindow(dosingHoursJson);
  if (!window) {
    return undefined;
  }

  if (notificationTime <= window.latestNotificationTime) {
    return undefined;
  }

  return `Notification time must be between 12:00 AM and ${formatTimeForMessage(
    window.latestNotificationTime
  )} local time based on ${capitalize(window.earliestClosingDay)} dosing hours.`;
}

function parseDosingRange(value: string) {
  const match = value
    .trim()
    .match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*-\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (!match) {
    return null;
  }

  const startMinutes = convertToMinutes(match[1], match[2], match[3]);
  const endMinutes = convertToMinutes(match[4], match[5], match[6]);
  if (startMinutes === null || endMinutes === null || endMinutes <= startMinutes) {
    return null;
  }

  return { startMinutes, endMinutes };
}

function convertToMinutes(hoursValue: string, minutesValue: string | undefined, meridiem: string) {
  const hours = Number(hoursValue);
  const minutes = Number(minutesValue ?? "0");
  if (
    !Number.isInteger(hours) ||
    !Number.isInteger(minutes) ||
    hours < 1 ||
    hours > 12 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }

  const normalizedMeridiem = meridiem.toLowerCase();
  const normalizedHours = hours % 12;
  const hourOffset = normalizedMeridiem === "pm" ? 12 : 0;
  return normalizedHours * 60 + hourOffset * 60 + minutes;
}

function formatMinutesAsTime(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function formatTimeForMessage(value: string) {
  const [hoursRaw, minutesRaw] = value.split(":");
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);
  const meridiem = hours >= 12 ? "PM" : "AM";
  const hours12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hours12}:${String(minutes).padStart(2, "0")} ${meridiem}`;
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
